import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { useDocumentLocale, useMicrocopy } from "../lib/microcopy";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./overlays";

export interface ReleaseNote { en: string; ar: string }
export interface Release { version: string; date: string; titleEn: string; titleAr: string; changed: ReleaseNote[]; added: ReleaseNote[]; removed: ReleaseNote[] }
/** `releases` is absent for anonymous callers (security §6 "Other": `/api/version` answers `{ version }` only before sign-in). */
export interface VersionInfo { version: string; commit?: string | null; builtAt?: string | null; releases?: Release[]; environment?: string }

const SEEN_KEY = "ncp.whatsnew.seen";
const SECTION = { changed: ["Changed", "ما تغير"], added: ["Added", "ما أضيف"], removed: ["Removed", "ما أزيل"] } as const;
const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function longDate(iso: string, ar: boolean): string {
  const [y, mo, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !mo || !d) return iso;
  return `${d} ${(ar ? MONTHS_AR : MONTHS_EN)[mo - 1]} ${y}`;
}

function newer(version: string, than: string | null): boolean {
  if (!than) return true;
  const a = version.split(".").map(Number);
  const b = than.split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d > 0;
  }
  return false;
}

/** The release this build shows notes for: the newest release whose number the build's version starts with. */
function currentRelease(info: VersionInfo): Release | undefined {
  const releases = info.releases ?? [];
  return releases.find((r) => info.version === r.version || info.version.startsWith(`${r.version}.`)) ?? releases[0];
}

/**
 * The version of the build (sidebar footer, sign-in page) and the bilingual "What's new" panel. The panel opens by itself once per
 * release after an update, and again from the version line.
 */
export function VersionBadge({ info, autoOpen = true, className }: { info: VersionInfo | undefined; autoOpen?: boolean; className?: string }) {
  const m = useMicrocopy();
  const docAr = useDocumentLocale() === "ar";
  // The note is issued in both languages (visual item 19): it opens in the screen's language and switches to the other in place.
  const [noteLang, setNoteLang] = useState<"ar" | "en" | null>(null);
  const ar = (noteLang ?? (docAr ? "ar" : "en")) === "ar";
  const [open, setOpen] = useState(false);
  const release = info ? currentRelease(info) : undefined;

  useEffect(() => {
    if (!autoOpen || !release) return;
    try {
      if (newer(release.version, window.localStorage.getItem(SEEN_KEY))) setOpen(true);
    } catch { /* storage unavailable */ }
  }, [autoOpen, release]);

  const close = (next: boolean) => {
    setOpen(next);
    if (!next && release) try { window.localStorage.setItem(SEEN_KEY, release.version); } catch { /* storage unavailable */ }
  };

  if (!info) return null;
  const label = `${m("version")} ${release?.version ?? info.version}`;
  // Before sign-in the API gives the version only: no notes to open.
  if (!info.releases?.length) return <span className={cn("inline-flex items-center px-2 py-1 text-xs text-muted-foreground", className)} data-version><bdi>{label}</bdi></span>;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-sand-100 hover:text-aegold-800", className)}
        title={info.commit ? `${info.version} · ${info.commit.slice(0, 7)}` : info.version} data-version>
        <Sparkles className="size-3.5 text-aegold-700" />
        <span><bdi>{label}</bdi> · {m("whatsNew")}</span>
      </button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent size="lg" className="max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{m("whatsNewTitle", { version: release?.version ?? info.version })}</DialogTitle>
            {release ? <DialogDescription>{ar ? release.titleAr : release.titleEn} · {longDate(release.date, ar)}</DialogDescription> : null}
          </DialogHeader>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setNoteLang(ar ? "en" : "ar")} lang={ar ? "en" : "ar"}>{ar ? "English" : "العربية"}</Button>
          </div>
          <div className="space-y-6" dir={ar ? "rtl" : "ltr"} lang={ar ? "ar" : "en"}>
            {(info.releases ?? []).map((r, i) => (
              <section key={r.version} className={cn("space-y-3", i > 0 && "border-t border-sand-200 pt-5")}>
                {i > 0 ? <h3 className="text-sm font-semibold">{m("version")} {r.version} · {ar ? r.titleAr : r.titleEn} · {longDate(r.date, ar)}</h3> : null}
                {([["changed", r.changed], ["added", r.added], ["removed", r.removed]] as const).map(([key, notes]) => notes.length > 0 ? (
                  <div key={key}>
                    <p className="mb-1.5 text-xs font-semibold text-aegold-800">{SECTION[key][ar ? 1 : 0]}</p>
                    <ul className="list-disc space-y-1 ps-5 text-sm leading-relaxed text-aeblack-900">
                      {notes.map((n, j) => <li key={j}>{ar ? n.ar : n.en}</li>)}
                    </ul>
                  </div>
                ) : null)}
              </section>
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={() => close(false)}>{m("close")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
